'use client'
import { PlusIcon, TrashIcon } from '@/_assets/icon-components'
import DebitCardIcon from '@/_assets/icon-components/DebitCard'
import ConfirmModal from '@/_components/ConfirmModal'
import FullScreenIframeDialog from '@/_components/IFrameDialog/IFrameDialog'
import paymentService from '@/_service/payment.service'
import { checkoutActions } from '@/_store/checkout/checkout.slice'
import { IRedux } from '@/_store/types'
import {
  addMessageListener,
  handleTiptopPayment,
} from '@/_utils/iframeLogic'
import { toaster } from '@/components/ui/toaster'
import {
  Button,
  HStack,
  IconButton,
  Stack,
  StackSeparator,
  Text,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

const MyCardsList = () => {
  const [cardId, setCardId] = useState('')
  const [blobUrl, setBlobUrl] = useState('')
  const [showIframeDialog, setShowIframeDialog] = useState(false)
  const t = useTranslations()
  const dispatch = useDispatch()

  const { data: settingsData } = useSelector((state: IRedux) => state.settings)

  const onlinePaymentType = useMemo(() => {
    return settingsData?.data?.payment_types?.find((type) =>
      ['epay', 'payze', 'tiptoppay', 'atmosaq'].includes(
        type?.integration || ''
      )
    )
  }, [settingsData?.data?.payment_types])

  const selectedIntegration = onlinePaymentType?.integration || ''

  const { data, refetch } = useQuery({
    queryKey: ['payment-saved-cards', selectedIntegration],
    queryFn: () =>
      paymentService
        .savedCards({ type: selectedIntegration })
        .then((res) => res.data),
    enabled: Boolean(selectedIntegration),
  })

  const onRemove = () => {
    paymentService
      .cardDelete(cardId, { type: onlinePaymentType?.integration || '' })
      .then(() => {
        toaster.success({
          title: t('successfully_deleted_card'),
        })
        refetch()
      })
    dispatch(checkoutActions.saveCard({}))
  }
  const onConfirmOnlineCard = () => {
    refetch()
  }
  const handleTiptop = () => {
    setShowIframeDialog(true)
    handleTiptopPayment(setBlobUrl)
  }

  const handleCloseIframe = useCallback(() => {
    setShowIframeDialog(false)
    setBlobUrl('')
  }, [])

  useEffect(() => {
    const removeListener = addMessageListener(
      handleCloseIframe,
      onConfirmOnlineCard
    )
    return removeListener
  }, [handleCloseIframe, onConfirmOnlineCard])

  if (
    onlinePaymentType?.integration === 'atmosaq' &&
    (data?.cards?.length || 0) === 0
  ) {
    return null
  }
  if (
    onlinePaymentType?.integration !== 'tiptoppay' &&
    onlinePaymentType?.integration !== 'atmosaq'
  ) {
    return null
  }

  return (
    <Stack flex={1} p={4} mt={4} bg='background.card' rounded={12}>
      <Text color={'text'} fontSize={20} fontWeight={700}>
        {t('my_cards')}
      </Text>
      <Stack separator={<StackSeparator />} gap={0}>
        {data?.cards?.map((el) => (
          <HStack
            key={el.id}
            justifyContent={'space-between'}
            py={4}
            gap={4}
            alignItems={'center'}
            color={'text'}
          >
            <HStack>
              <DebitCardIcon />
              <Text>{el.cardMask}</Text>
            </HStack>
            <IconButton variant={'ghost'} onClick={() => setCardId(el.id)}>
              <TrashIcon />
            </IconButton>
          </HStack>
        ))}
      </Stack>
      {onlinePaymentType?.integration === 'tiptoppay' && (
        <Button
          w='full'
          h='48px'
          rounded={16}
          variant='plain'
          color='primary'
          border='1px solid'
          borderColor='primary'
          _hover={{ bgColor: 'primary', color: 'light' }}
          size='lg'
          onClick={() => handleTiptop()}
        >
          <PlusIcon color='currentColor' />
          {t('add_new_card')}
        </Button>
      )}
      <ConfirmModal
        isOpen={Boolean(cardId)}
        onConfirm={onRemove}
        title={t('delete_card')}
        onClose={() => setCardId('')}
        text={{ confirm: 'confirm', cancel: 'cancel' }}
        description={t('confirm_delete_card')}
      />
      <FullScreenIframeDialog
        isOpen={showIframeDialog}
        onClose={() => {
          setShowIframeDialog(false)
          setBlobUrl('')
        }}
        url={blobUrl}
      />
    </Stack>
  )
}

export default MyCardsList
